/**
 * Arcade Effects Component
 * Displays combo counters, streaks, achievements, and particles
 */

import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { soundManager } from '../../utils/sounds';

interface ComboNotification {
  id: string;
  count: number;
  timestamp: number;
}

interface AchievementNotification {
  id: string;
  title: string;
  description: string;
  timestamp: number;
}

interface ParticleEffect {
  id: string;
  x: number;
  y: number;
  color: string;
  timestamp: number;
}

interface ArcadeEffectsState {
  combo: number;
  streak: number;
  notifications: ComboNotification[];
  achievements: AchievementNotification[];
  particles: ParticleEffect[];
}

export const ArcadeEffects: React.FC = () => {
  const [state, setState] = useState<ArcadeEffectsState>({
    combo: 0,
    streak: 0,
    notifications: [],
    achievements: [],
    particles: [],
  });

  useEffect(() => {
    // Auto-cleanup old notifications
    const interval = setInterval(() => {
      const now = Date.now();
      setState((prev) => ({
        ...prev,
        notifications: prev.notifications.filter((n) => now - n.timestamp < 3000),
        achievements: prev.achievements.filter((a) => now - a.timestamp < 5000),
        particles: prev.particles.filter((p) => now - p.timestamp < 1000),
      }));
    }, 100);

    return () => clearInterval(interval);
  }, []);

  // Listen for arcade events
  useEffect(() => {
    const handleCombo = (e: CustomEvent) => {
      const count = e.detail.count as number;
      setState((prev) => ({
        ...prev,
        combo: count,
        notifications: [
          ...prev.notifications,
          { id: Math.random().toString(), count, timestamp: Date.now() },
        ],
      }));

      if (count >= 3) {
        soundManager.play('combo');
      }
    };

    const handleAchievement = (e: CustomEvent) => {
      const { title, description } = e.detail as { title: string; description: string };
      setState((prev) => ({
        ...prev,
        achievements: [
          ...prev.achievements,
          { id: Math.random().toString(), title, description, timestamp: Date.now() },
        ],
      }));
      soundManager.play('achievement');
    };

    const handleParticle = (e: CustomEvent) => {
      const { x, y, color } = e.detail as { x: number; y: number; color: string };
      setState((prev) => ({
        ...prev,
        particles: [
          ...prev.particles,
          { id: Math.random().toString(), x, y, color, timestamp: Date.now() },
        ],
      }));
    };

    window.addEventListener('arcade:combo', handleCombo as EventListener);
    window.addEventListener('arcade:achievement', handleAchievement as EventListener);
    window.addEventListener('arcade:particle', handleParticle as EventListener);

    return () => {
      window.removeEventListener('arcade:combo', handleCombo as EventListener);
      window.removeEventListener('arcade:achievement', handleAchievement as EventListener);
      window.removeEventListener('arcade:particle', handleParticle as EventListener);
    };
  }, []);

  return (
    <div className="pointer-events-none fixed inset-0 z-50">
      {/* Combo Notifications */}
      <div className="absolute left-1/2 top-20 -translate-x-1/2">
        <AnimatePresence>
          {state.notifications.map((notification) => (
            <motion.div
              key={notification.id}
              initial={{ scale: 0, y: 50, opacity: 0 }}
              animate={{ scale: 1, y: 0, opacity: 1 }}
              exit={{ scale: 0, y: -50, opacity: 0 }}
              transition={{ type: 'spring', damping: 10, stiffness: 200 }}
              className="combo-popup mb-2"
            >
              <div className="flex flex-col items-center">
                <div className="neon-text font-display text-4xl font-bold">
                  {notification.count}x COMBO!
                </div>
                {notification.count >= 5 && (
                  <div className="neon-text-pink font-arcade text-xl">AMAZING!</div>
                )}
                {notification.count >= 10 && (
                  <div className="neon-text-pink font-arcade animate-bounce text-2xl">
                    LEGENDARY!!!
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Achievement Notifications */}
      <div className="absolute right-6 top-6 w-80">
        <AnimatePresence>
          {state.achievements.map((achievement) => (
            <motion.div
              key={achievement.id}
              initial={{ x: 400, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 400, opacity: 0 }}
              className="arcade-panel mb-4 rounded-lg p-4"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0">
                  <div className="neon-text text-3xl">🏆</div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="neon-text font-display text-sm font-bold">
                    {achievement.title}
                  </div>
                  <div className="mt-1 font-arcade text-xs text-white/80">
                    {achievement.description}
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {/* Particle Effects */}
      <AnimatePresence>
        {state.particles.map((particle) => (
          <motion.div
            key={particle.id}
            initial={{
              x: particle.x,
              y: particle.y,
              scale: 1,
              opacity: 1,
            }}
            animate={{
              x: particle.x + (Math.random() - 0.5) * 100,
              y: particle.y - 100,
              scale: 0,
              opacity: 0,
            }}
            transition={{ duration: 1, ease: 'easeOut' }}
            className="absolute"
            style={{
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              backgroundColor: particle.color,
              boxShadow: `0 0 10px ${particle.color}`,
            }}
          />
        ))}
      </AnimatePresence>

      {/* Persistent Combo/Streak Display */}
      {state.combo > 0 && (
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute left-6 top-6">
          <div className="arcade-panel rounded-lg px-4 py-2">
            <div className="font-display text-xs text-white/60">COMBO</div>
            <div className="neon-text font-display text-2xl">{state.combo}x</div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

// Helper functions to trigger effects from anywhere
export const triggerCombo = (count: number) => {
  window.dispatchEvent(
    new CustomEvent('arcade:combo', {
      detail: { count },
    }),
  );
};

export const triggerAchievement = (title: string, description: string) => {
  window.dispatchEvent(
    new CustomEvent('arcade:achievement', {
      detail: { title, description },
    }),
  );
};

export const triggerParticles = (x: number, y: number, color: string = '#00ffff') => {
  for (let i = 0; i < 10; i++) {
    window.dispatchEvent(
      new CustomEvent('arcade:particle', {
        detail: { x, y, color },
      }),
    );
  }
};

export const triggerScreenShake = (intensity: 'light' | 'medium' | 'heavy' = 'medium') => {
  const shakeClasses: Record<string, string> = {
    light: 'screen-shake-light',
    medium: 'screen-shake-medium',
    heavy: 'screen-shake-heavy',
  };

  const className = shakeClasses[intensity];
  document.body.classList.add(className);

  setTimeout(
    () => {
      document.body.classList.remove(className);
    },
    intensity === 'heavy' ? 200 : intensity === 'medium' ? 150 : 100,
  );
};

export const triggerFloatingScore = (
  x: number,
  y: number,
  score: number,
  color: string = '#4ef6ff',
) => {
  const el = document.createElement('div');
  el.className = 'floating-score';
  el.textContent = `+${score}`;
  el.style.cssText = `
    position: fixed;
    left: ${x}px;
    top: ${y}px;
    color: ${color};
    font-family: 'Press Start 2P', monospace;
    font-size: 16px;
    font-weight: bold;
    pointer-events: none;
    z-index: 9999;
    text-shadow: 0 0 10px ${color};
    animation: floatUp 1s ease-out forwards;
  `;
  document.body.appendChild(el);

  setTimeout(() => {
    el.remove();
  }, 1000);
};
